/**
 * Shell — unified coherent app shell (Option A, locked 2026-05-29).
 *
 * ONE layout for ALL signed-in views:
 *   sidebar (Sidebar.tsx) + sticky 52px top bar with ProfileMenu top-right.
 *
 * Workspace-aware: `mode` drives sidebar IA + breadcrumb workspace label.
 *   - 'individual' → personal IA (Dashboard, Catalogue, My data, My activity)
 *   - 'company'    → company IA + Account group (requires companyId)
 *   - 'admin'      → system-admin IA
 *
 * REPLACED:
 *   - Header top-nav for individual/company modes
 *   - AdminNav standalone sidebar (its IA is now part of Sidebar)
 *   - CompanyNav horizontal tab strip
 *   - WorkspaceSwitcher top-left pill (logic in ProfileMenu)
 *
 * `densityMode` and `items` are retained for backward-compat only; the authed
 * shell is always workspace density. Marketing/landing keeps its editorial Header.
 *
 * No `any` (Inv 13). No console.log (Inv 12). All nav uses <Link> (Inv 14).
 */

import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar, type SidebarWorkspace } from './Sidebar.tsx';
import { ProfileMenu } from './ProfileMenu.tsx';
import { apiFetch } from '@/lib/api-client';
import './shell.css';

export type ShellMode = 'individual' | 'admin' | 'company';
// densityMode retained for backward-compat — authed shell is always workspace density.
export type DensityMode = 'editorial' | 'workspace';

export interface ShellProps {
  /** Which workspace IA to render in the sidebar. */
  mode: ShellMode;
  /**
   * Density mode — retained for backward-compat with existing page callsites.
   * Ignored in the new shell; authed pages are always workspace density.
   */
  densityMode?: DensityMode;
  /**
   * Breadcrumb items — retained for backward-compat. The new top bar derives
   * its own breadcrumb from the URL; this prop is ignored.
   */
  items?: unknown[];
  /** Page content. */
  children?: ReactNode;
  /** Company ID — required when mode='company'. Falls back to active_company from whoami. */
  companyId?: string;
}

type WhoamiResponse =
  | {
      ok: true;
      data: {
        user: { id: string; email: string; name?: string };
        is_system_admin: boolean;
        active_company: null | { id: string; name: string; role: string };
      };
    }
  | { ok: false; error: { code: string; message: string } };

/** Map Shell mode to Sidebar workspace type. */
function toSidebarWorkspace(mode: ShellMode): SidebarWorkspace {
  if (mode === 'admin') return 'admin';
  if (mode === 'company') return 'company';
  return 'personal';
}

/**
 * Derive a human-readable page name from the current pathname for the top-bar
 * breadcrumb.  Strips /company/:id prefix so "/company/c1/members" → "Members".
 */
function pageLabel(pathname: string): string {
  const stripped = pathname.replace(/^\/company\/[^/]+/, '');
  const segment = stripped.split('/').filter(Boolean).pop() ?? '';
  const MAP: Record<string, string> = {
    dashboard: 'Dashboard',
    catalogue: 'Catalogue',
    members: 'Members',
    assignments: 'Assignments',
    attempts: 'Attempts',
    'share-tokens': 'Share tokens',
    'team-report': 'Team report',
    data: 'My data',
    activity: 'My activity',
    questions: 'Question bank',
    tests: 'Test composer',
    companies: 'Companies',
    'system-health': 'System health',
    'audit-log': 'Audit log',
    erase: 'GDPR erasure',
    'extended-time-grant': 'Extended-time grant',
    admin: 'Overview',
  };
  return (
    MAP[segment] ??
    segment
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ')
  );
}

export function Shell({
  mode,
  children,
  companyId,
}: ShellProps): ReactElement {
  const location = useLocation();
  const [userName, setUserName] = useState<string>('User');
  const [userEmail, setUserEmail] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  // Fallback for routes that don't carry :id (e.g. /company/assignments).
  const [activeCompanyId, setActiveCompanyId] = useState<string>('');
  // Mobile drawer state.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    async function loadUser(): Promise<void> {
      try {
        const res = await apiFetch('/api/admin/whoami', { credentials: 'same-origin' });
        const data = (await res.json()) as WhoamiResponse;
        if (data.ok && data.data.user) {
          setUserName(data.data.user.name ?? data.data.user.email);
          setUserEmail(data.data.user.email);
          if (data.data.active_company) {
            setActiveCompanyId(data.data.active_company.id);
            setCompanyName(data.data.active_company.name);
          }
        }
      } catch {
        // Leave defaults on error
      }
    }
    void loadUser();
  }, [mode]);

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const openDrawer = useCallback((): void => setDrawerOpen(true), []);
  const closeDrawer = useCallback((): void => {
    setDrawerOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  const effectiveCompanyId = companyId ?? activeCompanyId;

  const handleSignOut = (): void => {
    window.location.href = '/';
  };

  // Breadcrumb: "Workspace · Page"
  const pageName = pageLabel(location.pathname);
  const workspaceLabel =
    mode === 'company'
      ? (companyName || 'Company')
      : mode === 'admin'
      ? 'System Admin'
      : 'Personal';

  return (
    <div className="app-shell" data-testid="app-shell" data-mode={mode}>
      {/* Left sidebar — workspace-aware IA. On mobile renders as off-canvas drawer. */}
      <Sidebar
        workspace={toSidebarWorkspace(mode)}
        currentPathname={location.pathname}
        companyName={companyName || undefined}
        companyId={effectiveCompanyId}
        drawerOpen={drawerOpen}
        onDrawerClose={closeDrawer}
      />

      {/* Main column: sticky top bar + scrollable content */}
      <div className="app-main">
        <div className="app-top" role="banner">
          {/* Hamburger — mobile only, hidden on desktop via CSS */}
          <button
            ref={hamburgerRef}
            type="button"
            className="app-hamburger"
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls="app-sidebar"
            onClick={openDrawer}
            data-testid="hamburger-button"
          >
            <span className="app-hamburger-bar" />
            <span className="app-hamburger-bar" />
            <span className="app-hamburger-bar" />
          </button>

          <span className="app-crumb" aria-label="breadcrumb">
            {workspaceLabel} · <b>{pageName}</b>
          </span>
          <ProfileMenu
            userName={userName}
            userEmail={userEmail}
            onSignOut={handleSignOut}
            currentPathname={location.pathname}
          />
        </div>

        <div className="app-content">
          <main role="main">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
